import { IntercomScreen } from '@getstep/sdk/dist/store/intercom/Screens'
import { ErrorMessage } from '@hookform/error-message'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import { useForm } from 'react-hook-form'

import { SsoType } from '@app-types/sso'
import { TextInput } from '@components/input/text-input'
import { Legend } from '@contentful-components/forms/legend'
import type { FormProps } from '@contentful-components/forms/props'
import { CustomFieldId, useField } from '@graphql/hooks/field'
import { CustomFieldsetId } from '@graphql/hooks/form-page'
import { useDevMenuItem } from '@lib/hooks/useDevMenuItem'
import { useHelpCenterScreen } from '@lib/hooks/useHelpCenterScreen'
import { DevActionGroup, DevActionName } from '@lib/store/ui/dev-menu'
import { useSession } from '@lib/store/useStores'
import { LINK } from '@shared/util/link'
import { generateEmail } from '@shared/util/tests'

/**
 * This form lets the user enter their email.
 */
export const EnterEmail: FunctionComponent<FormProps<Values>> = ({
    next,
    title,
    renderSubmit,
}) => {
    const router = useRouter()
    const { emailVerificationStore } = useSession()

    const { label, placeholder, errorTexts } = useField(
        CustomFieldId.LoginEmail,
    )

    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { isSubmitting, errors, submitCount },
    } = useForm<Values>({
        defaultValues: { email: emailVerificationStore?.valueToVerify || '' },
    })

    useHelpCenterScreen(IntercomScreen.SIGNUP_ENTER_EMAIL_CODE)

    useDevMenuItem({
        group: DevActionGroup.PAGE,
        name: DevActionName.FILL_EMAIL,
        message: 'Filled the form with a test email address 💌',
        async onActivate() {
            setValue('email', await generateEmail(), {
                shouldDirty: true,
                shouldValidate: true,
            })
        },
    })

    if (!emailVerificationStore) {
        return null // or a loading state
    }

    return (
        <form
            onSubmit={handleSubmit(async ({ email }) => {
                await emailVerificationStore.startVerification(email, next)
                router.push(
                    LINK.verifySSO(SsoType.Email, next).serialize(router),
                )
            })}
        >
            <Legend
                preset='primary'
                customFieldsetId={CustomFieldsetId.LoginEnterEmail}
            >
                {title}
            </Legend>
            <TextInput
                autoFocus
                type='email'
                preset='material'
                disabled={isSubmitting}
                validationCount={submitCount}
                size='medium'
                label={label}
                placeholder={placeholder}
                valid={!errors.email}
                error={<ErrorMessage errors={errors} name='email' />}
                data-testid='login.email.input'
                {...register('email', {
                    required: {
                        value: true,
                        message: errorTexts.required,
                    },
                    pattern: {
                        value: /[^\s@]+@[^\s@]+\.[^\s@]+/i,
                        message: errorTexts.required,
                    },
                })}
            />
            {renderSubmit(control)}
        </form>
    )
}

interface Values {
    email: string
}
